'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  CheckoutPrewarmParams,
  useCheckoutPrewarm,
} from '@/features/subscription/contexts/CheckoutPrewarm';

type UseCheckoutIntentPrefetchParams = {
  getPriceParams: () => CheckoutPrewarmParams | undefined;
  enabled?: boolean;
};

export function useCheckoutIntentPrefetch({
  getPriceParams,
  enabled = true,
}: UseCheckoutIntentPrefetchParams) {
  const { prefetchCheckout } = useCheckoutPrewarm();
  const lastFiredPriceIdRef = useRef<string | null>(null);
  const lastFiredPromiseRef = useRef<Promise<string | null> | null>(null);
  const pendingIntentRef = useRef(false);

  const fire = useCallback(() => {
    if (!enabled) {
      return undefined;
    }

    const params = getPriceParams();
    if (!params?.priceId) {
      // Intent fired before pricing finished loading (e.g. file selected
      // right after page load). Remember it and retry once prices arrive,
      // instead of silently losing the head start.
      pendingIntentRef.current = true;
      return undefined;
    }

    if (lastFiredPriceIdRef.current === params.priceId) {
      // Already prefetching (or prefetched) this exact price; hand back the
      // same promise so callers can still await readiness.
      return lastFiredPromiseRef.current ?? undefined;
    }

    pendingIntentRef.current = false;
    lastFiredPriceIdRef.current = params.priceId;
    const promise = prefetchCheckout(params).then((transactionId) => {
      if (transactionId === null) {
        lastFiredPriceIdRef.current = null;
        pendingIntentRef.current = true;
      }
      return transactionId;
    });
    lastFiredPromiseRef.current = promise;
    return promise;
  }, [enabled, getPriceParams, prefetchCheckout]);

  useEffect(() => {
    if (pendingIntentRef.current) {
      fire();
    }
  }, [fire]);

  return {
    intentHandlers: {
      onPointerEnter: fire,
      onFocus: fire,
    },
    triggerPrefetch: fire,
  };
}
